import React, { useState } from "react";

/**
 * OnboardingChecklist Component (Phase 0.8)
 *
 * Reusable onboarding checklist for different user types.
 * Shows progress and encourages profile completion.
 *
 * Props:
 * - userType: 'owner', 'freelancer', 'professional', 'customer'
 * - completedSteps: array of completed step IDs
 * - onStepClick: callback when user clicks a step
 * - onDismiss: callback to dismiss checklist
 */
const OnboardingChecklist = ({
  userType = "owner",
  completedSteps = [],
  onStepClick,
  onDismiss,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);

  // Define steps for each user type
  const stepsByType = {
    owner: [
      {
        id: "profile",
        label: "Complete business profile",
        action: "/dashboard/settings/profile",
      },
      {
        id: "logo",
        label: "Upload business logo",
        action: "/dashboard/settings/profile",
      },
      {
        id: "location",
        label: "Add business location",
        action: "/dashboard/locations",
      },
      {
        id: "hours",
        label: "Set business hours",
        action: "/dashboard/locations",
      },
      { id: "payment", label: "Connect payment account", action: "/dashboard" },
    ],
    freelancer: [
      {
        id: "photo",
        label: "Upload professional headshot",
        action: "/dashboard/freelancer/profile",
      },
      {
        id: "services",
        label: "Add services and pricing",
        action: "/dashboard/freelancer/services",
      },
      {
        id: "schedule",
        label: "Set your availability",
        action: "/dashboard/freelancer/schedule",
      },
      {
        id: "payment",
        label: "Connect payment account",
        action: "/dashboard/freelancer/payment",
      },
    ],
    professional: [
      {
        id: "photo",
        label: "Add profile photo",
        action: "/dashboard/professional/profile",
      },
      {
        id: "services",
        label: "List your services",
        action: "/dashboard/professional/services",
      },
      {
        id: "schedule",
        label: "Configure your schedule",
        action: "/dashboard/professional/schedule",
      },
    ],
    customer: [
      { id: "photo", label: "Add profile photo", action: "/profile" },
      {
        id: "preferences",
        label: "Set your preferences",
        action: "/profile/preferences",
      },
      { id: "booking", label: "Make your first booking", action: "/shops" },
      {
        id: "notification",
        label: "Enable notifications",
        action: "/profile/settings",
      },
    ],
  };

  const steps = stepsByType[userType] || stepsByType.owner;
  const completedCount = steps.filter((step) =>
    completedSteps.includes(step.id),
  ).length;
  const progress = Math.round((completedCount / steps.length) * 100);
  const isComplete = completedCount === steps.length;

  // Color schemes by user type
  const colors = {
    owner: {
      gradient: "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
      text: "#1a659e",
      bg: "#e8f4f8",
    },
    freelancer: {
      gradient: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
      text: "#2d6da3",
      bg: "#f0f4ff",
    },
    professional: {
      gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      text: "#3b82f6",
      bg: "#dbeafe",
    },
    customer: {
      gradient: "linear-gradient(135deg, #ec4899 0%, #f59e0b 100%)",
      text: "#ec4899",
      bg: "#fef2f2",
    },
  };

  const theme = colors[userType] || colors.owner;

  if (isComplete && onDismiss) {
    return (
      <div
        style={{
          background: "#dcfce7",
          border: "1px solid #86efac",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "24px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "48px", marginBottom: "8px" }}>🎉</div>
        <h3 style={{ margin: "0 0 8px", color: "#065f46", fontSize: "20px" }}>
          Setup Complete!
        </h3>
        <p style={{ margin: "0 0 16px", color: "#047857", fontSize: "14px" }}>
          You're all set! Your profile is complete and ready to go.
        </p>
        <button
          onClick={onDismiss}
          style={{
            padding: "8px 20px",
            background: "#10b981",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          Dismiss
        </button>
      </div>
    );
  }

  if (isMinimized) {
    return (
      <div
        onClick={() => setIsMinimized(false)}
        style={{
          background: theme.gradient,
          borderRadius: "12px",
          padding: "16px 20px",
          marginBottom: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              background: "rgba(255,255,255,0.2)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "20px",
            }}
          >
            📋
          </div>
          <div>
            <div
              style={{ color: "white", fontWeight: "600", fontSize: "16px" }}
            >
              Setup Progress: {progress}%
            </div>
            <div style={{ color: "rgba(255,255,255,0.9)", fontSize: "13px" }}>
              {completedCount} of {steps.length} completed
            </div>
          </div>
        </div>
        <div style={{ color: "white", fontSize: "20px" }}>▼</div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "white",
        border: "2px solid #e5e7eb",
        borderRadius: "12px",
        padding: "24px",
        marginBottom: "24px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "start",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div>
          <h3
            style={{
              margin: "0 0 4px",
              color: "#111827",
              fontSize: "20px",
              fontWeight: "700",
            }}
          >
            🚀 Complete Your Setup
          </h3>
          <p style={{ margin: 0, color: "#6b7280", fontSize: "14px" }}>
            {completedCount} of {steps.length} steps completed
          </p>
        </div>
        <button
          onClick={() => setIsMinimized(true)}
          style={{
            padding: "4px 8px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "#9ca3af",
            fontSize: "18px",
          }}
          title="Minimize"
        >
          ▲
        </button>
      </div>

      {/* Progress Bar */}
      <div
        style={{
          width: "100%",
          height: "8px",
          background: "#e5e7eb",
          borderRadius: "4px",
          marginBottom: "20px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            background: theme.gradient,
            transition: "width 0.3s ease",
            borderRadius: "4px",
          }}
        />
      </div>

      {/* Steps */}
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {steps.map((step) => {
          const isCompleted = completedSteps.includes(step.id);
          return (
            <div
              key={step.id}
              onClick={() => !isCompleted && onStepClick && onStepClick(step)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px",
                background: isCompleted ? theme.bg : "#f9fafb",
                border: `2px solid ${isCompleted ? theme.text : "#e5e7eb"}`,
                borderRadius: "8px",
                cursor: isCompleted ? "default" : "pointer",
                transition: "all 0.2s",
                opacity: isCompleted ? 0.7 : 1,
              }}
            >
              {/* Checkbox */}
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  background: isCompleted ? theme.text : "white",
                  border: `2px solid ${isCompleted ? theme.text : "#d1d5db"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {isCompleted && (
                  <span style={{ color: "white", fontSize: "14px" }}>✓</span>
                )}
              </div>

              {/* Label */}
              <div
                style={{
                  flex: 1,
                  color: isCompleted ? theme.text : "#374151",
                  fontSize: "15px",
                  fontWeight: "500",
                  textDecoration: isCompleted ? "line-through" : "none",
                }}
              >
                {step.label}
              </div>

              {/* Arrow */}
              {!isCompleted && (
                <div style={{ color: "#9ca3af", fontSize: "16px" }}>→</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Encouragement */}
      {progress < 100 && progress > 0 && (
        <div
          style={{
            marginTop: "20px",
            padding: "12px",
            background: "#fef3c7",
            border: "1px solid #fbbf24",
            borderRadius: "8px",
            fontSize: "13px",
            color: "#92400e",
            textAlign: "center",
          }}
        >
          <strong>Keep going!</strong> Complete all steps to maximize your
          visibility.
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist;
